
import { Component, Emit, Prop, Vue } from "vue-property-decorator";
import vSelect3 from "vselect3";
import { Getter, namespace } from 'vuex-class';
import { CardValidation } from "@/store/rules-engine/types";
import { RuleAlarmTag as TagSummaryObject } from "@/store/rules-engine/types"

export enum RuleAlarmTagAction {
    Add = "Add",
    Remove = "Remove"
}

const RuleStore = namespace("rulesEngine");

@Component({
	components: {
			"v-select-3": vSelect3,
	}
})
export default class RuleAlarmTag extends Vue {
    @Prop({type: String, default: ""})
    private action!: RuleAlarmTagAction;

    @Prop({type: Array, default: () => []})
    private value!: number[];

    @Getter private getUserTenantGroupId!: number;
    @RuleStore.State private alarmTags: TagSummaryObject[];

    private selectedTags: number[] = [];

	private inputValidation: CardValidation = { isValid: false, message: ""};

    private mounted(): void {
        this.selectedTags = this.value;
        this.emitIsValid();
    }

    private get isNonTenancyTagsSelected(): boolean {
        if (!this.selectedTags || this.selectedTags.length == 0) {
            return false;
        }
        const tagSummaryObjects = this.alarmTags?.filter(tag => this.selectedTags.includes(tag.alarmTagId));
        return tagSummaryObjects?.some(tag => tag.groupId !== this.getUserTenantGroupId) ?? false;
    }

    @Emit("input")
    private emitSelectedTags() {
        this.emitIsValid();
        return this.selectedTags;
    }

    @Emit("isValid")
    private emitIsValid() {
        this.inputValidation = this.getInputValidationObject;
        return this.inputValidation;
    }

    private get getInputValidationObject() {
        if (!this.selectedTags || this.selectedTags.length == 0) {
            return { isValid: false, message: "At least one tag must be selected." };
        }

        // If any of the selected tags are not present in the alarmTags options, 
        // then they have been deleted from the tenancy and we should show an error message and prevent saving until they are removed from the selection.
        if (this.selectedTags.some(tag => !this.alarmTags.find(option => option.alarmTagId == tag))) {
            return { isValid: false, message: "One or more selected tags have been deleted for your tenancy. Please remove them to proceed." };
        }

        return { isValid: true, message: "" };
    }

    // Deleted options are detected by missing both title and groupId.
    // v-select maps unknown IDs to the title field only, so we can identify deleted tags without extra lookups.
    private isOptionDeleted(option: TagSummaryObject): boolean {
        return !(option.title && option.groupId);
    }

    private renderOptionText(option: TagSummaryObject): string {
        if (!this.isOptionDeleted(option)) {
            return option.title;
        }
        return "Deleted Tag: Remove to Save";
    }

    private isOptionHighlighted(option: TagSummaryObject): boolean {
        return this.isOptionDeleted(option);
    }

    private get placeholderText(): string {
        switch (this.action) {
            case RuleAlarmTagAction.Add:
                return "Select Alarm Tags to Add";
            case RuleAlarmTagAction.Remove:
                return "Select Alarm Tags to Remove";
            default:
                return "Select Alarm Tags";
        }
    }

    private reducerFunction(tag: TagSummaryObject): number {
        // Implementing a reducer function to map the selected option objects to their corresponding alarmTagId values for v-select
        // And handling the case where the option is not present in the alarmTags options (i.e. it has been deleted from the tenancy) 
        return tag?.alarmTagId;
    }
}
